import type { HandoffPayload } from "./types";

const Prefix = "SV1";

export function encodeHandoff(payload: HandoffPayload): string {
  validatePayload(payload);
  const canonical = canonicalPayload(payload);
  const crc = crc16Ccitt(new TextEncoder().encode(canonical));
  return `${Prefix}:${base64UrlEncode(canonical)}:${crc.toString(16).padStart(4, "0")}`;
}

export function decodeHandoff(input: string): HandoffPayload {
  const [prefix, encoded, crcHex] = input.trim().split(":");
  if (prefix !== Prefix || !encoded || !crcHex) {
    throw new Error("Invalid SkyView handoff framing.");
  }
  const canonical = base64UrlDecode(encoded);
  if (!canonical.startsWith('{"v":')) {
    throw new Error("Payload version must be first.");
  }
  const version = JSON.parse(canonical.slice(0, canonical.indexOf(",") > 0 ? canonical.indexOf(",") + 1 : canonical.length).replace(/,$/, "}")) as { v?: number };
  if (version.v !== 1) {
    throw new Error(`Unsupported handoff version: ${String(version.v)}`);
  }
  const expectedCrc = Number.parseInt(crcHex, 16);
  const actualCrc = crc16Ccitt(new TextEncoder().encode(canonical));
  if (expectedCrc !== actualCrc) {
    throw new Error("Handoff checksum mismatch.");
  }
  const payload = JSON.parse(canonical) as HandoffPayload;
  validatePayload(payload);
  return payload;
}

export function canonicalPayload(payload: HandoffPayload): string {
  const fields: string[] = [`"v":${payload.v}`];
  if (payload.lat !== undefined) fields.push(`"lat":${trimNumber(payload.lat)}`);
  if (payload.lon !== undefined) fields.push(`"lon":${trimNumber(payload.lon)}`);
  if (payload.t !== undefined) fields.push(`"t":${payload.t}`);
  fields.push(`"ids":[${payload.ids.join(",")}]`);
  return `{${fields.join(",")}}`;
}

export function crc16Ccitt(bytes: Uint8Array): number {
  let crc = 0xffff;
  for (const byte of bytes) {
    crc ^= byte << 8;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc & 0x8000) !== 0 ? ((crc << 1) ^ 0x1021) : (crc << 1);
      crc &= 0xffff;
    }
  }
  return crc;
}

function validatePayload(payload: HandoffPayload): void {
  if (payload.v !== 1) throw new Error(`Unsupported handoff version: ${String(payload.v)}`);
  if (!Array.isArray(payload.ids) || payload.ids.length === 0) {
    throw new Error("Handoff payload must contain at least one target.");
  }
  for (const id of payload.ids) {
    if (!Number.isInteger(id) || id < 1 || id > 9110) {
      throw new Error(`Invalid HR target id: ${id}`);
    }
  }
}

function trimNumber(value: number): string {
  return Number(value.toFixed(6)).toString();
}

function base64UrlEncode(value: string): string {
  const binary = String.fromCharCode(...new TextEncoder().encode(value));
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function base64UrlDecode(value: string): string {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const binary = atob(padded);
  return new TextDecoder().decode(Uint8Array.from(binary, (char) => char.charCodeAt(0)));
}
